import { Capacitor } from '@capacitor/core';
import EposPlugin, { PrinterBuilder } from 'capacitor-epos';
import StarXpandPlugin from 'capacitor-starxpand';
import { useEffect, useRef } from 'react';
import { DELIVERY_FEE, ROLES } from '../consts/language';
import { useAuthContext } from '../context/AuthContext/AuthContext';
import { usePrinterContext } from '../context/PrinterContext';
import { useSettingsContext } from '../context/SettingsContext';
import { getDoc } from '../libs/firestore-wrapper';
import { getCurrentSettings } from '../modals/order';
import { IFood, IOrders, ITopic } from '../pages/Dashboard/order.types';
import { ISettings, IStadium } from '../pages/Settings/settings.types';
import { fetchMultipleSeatsByTableNumbers, formatPriceItem } from '../utils';
import { formatHourMonthDayYear } from '../utils/handleDateTime';
import { formatSeatInfo } from '../utils/seatHelpers';
import useLogPrinterStatus from './useLogPrinterStatus';
import { usePrinterDiscovery } from './usePrinterDiscovery';
import { usePrinterQueue } from './usePrinterQueue';

const COLOR_1 = 1;
const COLOR_2 = 2;
const IP_CONNECT_ATTEMPTS = 5;
const IP_CONNECT_DELAY_MS = 800;
const BT_CONNECT_ATTEMPTS = 5;
const BT_CONNECT_DELAY_MS = 600;

type PrinterBrand = 'star' | 'epson';

interface IPrintOrder {
  ip_printer?: string;
  data?: IOrders;
  dataSettings?: ISettings;
  stadium?: IStadium;
  type?: string;
  printer_brand?: PrinterBrand;
}

const usePrinterNative = () => {
  const { user, userInfo } = useAuthContext();
  const { mutateAsync } = useLogPrinterStatus();
  const { currentSetting, stadiumData } = useSettingsContext();
  const large_paper = currentSetting.large_paper ?? false;

  const connection = usePrinterContext();
  const discovery = usePrinterDiscovery(connection.bluetoothEnabled);
  const queue = usePrinterQueue();
  const restaurantNameCache = useRef<Record<string, string>>({});

  const printHeaderSection = async (builder: PrinterBuilder, data: IOrders, is_preorder?: boolean, stadium?: IStadium) => {
    builder.addTextAlign('left');
    builder.addFeedLine(1);
    builder.addTextPosition(10);
    const _preorderLabel = data?.is_pre_order ? 'PREORDER' : '';
    builder.addText(`Order ID: ${data.id}${_preorderLabel}\n`);
    builder.addTextPosition(20);
    if (data?.requested_delivery_time) {
      const displayDelivery =
        data?.delivery_request === 'WHEN_ITS_READY'
          ? `${formatHourMonthDayYear(data.requested_delivery_time).slashDate} 指定なし`
          : data?.delivery_request
            ? data.delivery_request
            : formatHourMonthDayYear(data.requested_delivery_time).fullDateTime;
      builder.addTextSize(2, 2);
      builder.addText(`配達予定時間:\n`);
      builder.addText(`${displayDelivery}\n\n`);
      builder.addTextSize(1, 1);
    } else {
      builder.addText(`Date: ${formatHourMonthDayYear(data.order_create_time).fullDateTime}\n\n`);
    }
    if (is_preorder) {
      builder.addTextPosition(20);
      builder.addText(`座席番号\n`);
      builder.addTextPosition(20);
      let seatLabel = '';
      const seatId = data?.order_table_number;
      const stadiumId = stadium?.id || stadiumData?.id;
      if (seatId && stadiumId) {
        try {
          const m = await fetchMultipleSeatsByTableNumbers(stadiumId, [seatId], true);
          const info = m.get(seatId);
          const formatted = formatSeatInfo(info);
          if (formatted) seatLabel = formatted;
        } catch (e) {
          // ignore
        }
      }
      const out = seatLabel;
      builder.addText(`${out}\n`);
    }
  };

  const printFoodItem = (builder: PrinterBuilder, item: IFood) => {
    const maxCharsPerLineFontA = large_paper ? 43 : 29;
    const extraChars = 8;
    const quantity = item.item_deleted_quantity !== undefined ? item.item_quantity - item.item_deleted_quantity : item.item_quantity;
    builder.addTextPosition(30);
    builder.addText(`${quantity} x\t${wrapText(item.item_name, extraChars, maxCharsPerLineFontA - 5)}\n`);
    builder.addTextStyle({ reverse: false, underline: false, bold: false, color: COLOR_2 });
    if (item.item_customize_settings && item.item_customize_settings.length > 0) {
      item.item_customize_settings.forEach((topping: ITopic) => {
        builder.addTextPosition(30);
        builder.addText(`  - ${topping.name}\n`);
      });
    }
    if (item.item_note !== '') {
      builder.addTextPosition(30);
      builder.addText(`  note: ${item.item_note}\n\n`);
    }
    builder.addTextStyle({ reverse: false, underline: false, bold: false, color: COLOR_1 });
  };

  const calculateTotal = (data: IOrders) => {
    let totalPrice = 0;
    let totalCount = 0;
    let totalTax10 = DELIVERY_FEE;
    let totalTax8 = 0;
    let totalTaxAmount10 = 0;
    let totalTaxAmount8 = 0;

    data.foods.forEach((item: IFood) => {
      const quantity = item.item_deleted_quantity !== undefined ? item.item_quantity - item.item_deleted_quantity : item.item_quantity;
      const itemPrice = quantity * item.item_price;
      totalPrice += itemPrice;
      totalCount += quantity;

      const taxRate = parseFloat(item.tax_rate) ?? 8;
      const totalInclusive = itemPrice;
      let taxAmount = 0;
      if (totalInclusive > 0 && taxRate > 0) {
        const beforeTax = Math.round(totalInclusive / (1 + taxRate / 100));
        taxAmount = totalInclusive - beforeTax;
      }
      if (taxRate === 10) {
        totalTaxAmount10 += taxAmount;
        totalTax10 += totalInclusive;
      } else {
        totalTaxAmount8 += taxAmount;
        totalTax8 += totalInclusive;
      }
    });

    return {
      totalPrice,
      totalCount,
      totalTax10,
      totalTax8,
      totalTaxAmount10: totalTaxAmount10 > 0 ? totalTaxAmount10 + 77 : 70,
      totalTaxAmount8,
    };
  };

  const fetchRestaurantName = async (restaurantId: string): Promise<string> => {
    if (!restaurantId) return '';
    const cache = restaurantNameCache.current;
    if (cache[restaurantId]) return cache[restaurantId];
    try {
      const snap = await getDoc(getCurrentSettings(restaurantId));
      if (snap && snap.exists()) {
        const d: any = snap.data();
        const name = d?.name || '';
        cache[restaurantId] = name;
        return name;
      }
    } catch (e) {
      // ignore
    }
    return '';
  };

  const printOrderDetails = async (builder: PrinterBuilder, data: IOrders, is_preorder?: boolean) => {
    if (!is_preorder) {
      data.foods.forEach((item: IFood) => {
        printFoodItem(builder, item);
      });
      return;
    }

    const getAllowedRestaurantIds = (): string[] => {
      let allowedRestaurantIds: string[] = [];
      if (userInfo?.role === ROLES.SUPER_ADMIN && Array.isArray(userInfo.restaurants)) {
        allowedRestaurantIds = userInfo.restaurants.filter((r: any) => r.is_admin_store).map((r: any) => r.id);
      } else if (userInfo?.restaurant_id) {
        allowedRestaurantIds = [userInfo.restaurant_id];
      }

      if (allowedRestaurantIds.length === 0 && data.foods.length > 0) {
        const firstRid = (data.foods[0] as any).restaurantId ?? null;
        if (firstRid) allowedRestaurantIds = [firstRid];
      }
      return allowedRestaurantIds;
    };

    const printGroupedFoods = async (foods: IFood[]) => {
      const groups: Record<string, IFood[]> = {};
      foods.forEach((f: IFood) => {
        const rid = (f as IFood).restaurantId ?? '';
        if (!groups[rid]) groups[rid] = [];
        groups[rid].push(f);
      });

      for (const rid of Object.keys(groups)) {
        const foods = groups[rid];
        const name = (await fetchRestaurantName(rid)) || rid;
        builder.addTextAlign('center');
        builder.addTextPosition(30);
        builder.addText(`\n${name}\n`);
        builder.addTextAlign('left');
        foods.forEach((item: IFood) => {
          printFoodItem(builder, item);
        });
      }
    };

    if (is_preorder) {
      const allowedRestaurantIds = getAllowedRestaurantIds();
      const filteredFoods = data.foods.filter((f: IFood) => allowedRestaurantIds.includes((f as any).restaurantId));
      await printGroupedFoods(filteredFoods);
      return;
    }
  };

  const formatRowWithSpacing = (label: string, count: number | string, sub_price: number, isUnitPrice: boolean, totalLength: number, price: number): string => {
    const countText = `${count}`;
    const priceText = `¥${formatPriceItem(sub_price)}`;
    const fixedLeftSpace = large_paper ? totalLength - 28 : totalLength - 14;
    const usedLength = 3 + countText.length + priceText.length + fixedLeftSpace;
    const remainingSpaces = totalLength - usedLength;

    if (isUnitPrice) {
      const fixedLeftSpaceForEachPart = large_paper ? 8 : 4;
      const leftSpaceForLabel = fixedLeftSpace - (fixedLeftSpaceForEachPart + price.toString().length);
      return label + ' '.repeat(fixedLeftSpaceForEachPart) + `@${price}` + ' '.repeat(leftSpaceForLabel) + countText + ' '.repeat(remainingSpaces) + priceText;
    }

    return label + ' '.repeat(fixedLeftSpace) + countText + ' '.repeat(remainingSpaces) + priceText;
  };

  const formatRowWithAlignment = (label: string, value: string | number, totalLength: number): string => {
    let labelLength = 0;
    for (const char of label) {
      labelLength += /[^\x00-\x7F]/.test(char) ? 2 : 1;
    }
    const valueText = `¥${formatPriceItem(value)}`;
    const valueLength = valueText.length;
    const remainingSpaces = totalLength - (labelLength + valueLength);
    return label + ' '.repeat(Math.max(0, remainingSpaces)) + valueText;
  };

  const printTotalSection = (
    data: IOrders,
    builder: PrinterBuilder,
    totalCount: number,
    totalPrice: number,
    totalTax10: number,
    totalTaxAmount10: number,
    totalTax8: number,
    totalTaxAmount8: number,
    dataSettings?: ISettings
  ) => {
    const maxCharsPerLineFontA = large_paper ? 43 : 29;
    const maxCharsPerLineFontB = large_paper ? 52 : 35;
    const maxCharsPerLineFontALarge = large_paper ? 22 : 15;
    builder.addText('\n');
    builder.addTextFont('A');
    builder.addTextPosition(30);
    builder.addText(formatRowWithSpacing('小計', totalCount, totalPrice, false, maxCharsPerLineFontA, 0) + '\n\n');
    builder.addTextPosition(30);
    builder.addText(formatRowWithAlignment('配達手数料', DELIVERY_FEE, maxCharsPerLineFontA + 1) + '\n\n');
    builder.addTextStyle({ reverse: false, underline: false, bold: true, color: COLOR_1 });
    builder.addTextFont('A');
    builder.addTextSize(2, 2);
    builder.addTextPosition(30);
    builder.addText(formatRowWithAlignment('合計', totalPrice + DELIVERY_FEE, maxCharsPerLineFontALarge) + '\n\n');
    builder.addTextSize(1, 1);
    builder.addTextPosition(30);
    builder.addTextFont('B');
    builder.addTextStyle({ reverse: false, underline: false, bold: false, color: COLOR_1 });
    builder.addText(formatRowWithAlignment('10%対象計', totalTax10, maxCharsPerLineFontB) + '\n');
    builder.addTextPosition(30);
    builder.addText(formatRowWithAlignment('(内消費税等', totalTaxAmount10, maxCharsPerLineFontB) + ')\n');
    builder.addTextPosition(30);
    builder.addText(formatRowWithAlignment('8%対象計', totalTax8, maxCharsPerLineFontB) + '\n');
    builder.addTextPosition(30);
    builder.addText(formatRowWithAlignment('(内消費税等', totalTaxAmount8, maxCharsPerLineFontB) + ')\n');
    builder.addTextPosition(30);
    builder.addText('※軽減税率 (8%）対象商品\n\n');
    builder.addTextPosition(30);
    builder.addText(`店舗: ${dataSettings ? (dataSettings.merchant_id ?? '') : (currentSetting.merchant_id ?? '')}\n`);
    builder.addTextPosition(30);
    builder.addText(`担当者: ${user?.email ?? ''}\n`);
    builder.addTextPosition(30);
    builder.addText(`取引番号: ${data.id}\n`);
  };

  const fetchImageAsBase64 = async (imageUrl: string): Promise<string> => {
    try {
      const response = await fetch(imageUrl, { mode: 'cors' });
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
      });
    } catch (error) {
      return '';
    }
  };

  const printStadiumInformation = async (builder: PrinterBuilder, dataSettings?: ISettings, stadium?: IStadium) => {
    const imageUrl = stadium?.stadium_profile_pic || stadiumData?.stadium_profile_pic;
    if (imageUrl) {
      const base64Image = await fetchImageAsBase64(imageUrl);
      if (base64Image) {
        let rawBase64 = base64Image;
        try {
          if (rawBase64.startsWith('data:')) {
            const parts = rawBase64.split(',');
            rawBase64 = parts.length > 1 ? parts[1] : parts[0];
          }

          if (!rawBase64 || rawBase64.length < 50) {
          } else {
            const width = large_paper ? 546 : 384;
            const height = large_paper ? 450 : 290;
            try {
              builder.addImage({ data: rawBase64, width, height });
            } catch (err: any) {}
          }
        } catch (err: any) {}
      }
    }
    builder.addText('\n');
    builder.addTextAlign('center');
    builder.addTextPosition(30);
    builder.addText('領収証\n\n');
    await printBusinessDetails(builder, stadium);
    builder.addTextAlign('left');
    builder.addTextPosition(30);
    builder.addText(`${dataSettings ? (dataSettings.name ?? 'Restaurant') : (currentSetting.name ?? 'Restaurant')}\n`);
  };

  const printReceiptDetails = async (builder: PrinterBuilder, data: IOrders, dataSettings?: ISettings, stadium?: IStadium) => {
    const maxCharsPerLineFontA = large_paper ? 43 : 29;
    await printStadiumInformation(builder, dataSettings, stadium);
    builder.addTextPosition(30);
    builder.addText(
      formatDateTimeWithAlignment(formatHourMonthDayYear(data.order_create_time).japaneseDate, formatHourMonthDayYear(data.order_create_time).time) + '\n\n'
    );
    const extraChars = 3;
    data.foods.forEach((item: IFood) => {
      builder.addTextPosition(30);
      builder.addText(wrapText(item.item_name, extraChars, maxCharsPerLineFontA) + '\n');
      builder.addTextPosition(30);
      const quantity = item.item_deleted_quantity !== undefined ? item.item_quantity - item.item_deleted_quantity : item.item_quantity;
      const price = quantity === 0 ? 0 : item.item_price;
      const total = parseFloat((quantity * price).toFixed(2));
      builder.addText(formatRowWithSpacing('※*', quantity, total, true, maxCharsPerLineFontA, price) + '\n');
    });
  };

  const printBusinessDetails = async (builder: PrinterBuilder, stadium?: IStadium) => {
    if (stadium?.address || stadiumData.address) {
      builder.addTextAlign('center');
      builder.addTextPosition(30);
      builder.addText(`${stadium?.address || stadiumData.address}\n`);

      builder.addTextAlign('center');
      builder.addTextPosition(30);
      builder.addText(`TEL: ${stadium?.phone || stadiumData.phone}\n`);
    }

    if (stadium?.registration_code || stadiumData.registration_code) {
      builder.addTextAlign('left');
      builder.addTextPosition(30);
      builder.addText(`登録番号: ${stadium?.registration_code || stadiumData.registration_code}\n`);
    }

    if (stadium?.business_name || stadiumData.business_name) {
      builder.addTextAlign('left');
      builder.addTextPosition(30);
      builder.addText(`事業者名: ${stadium?.business_name || stadiumData.business_name}\n`);
    }
  };

  const formatDateTimeWithAlignment = (leftText: string, rightText: string): string => {
    const maxCharsPerLineFontA = large_paper ? 43 : 29;
    const leftTextLength = 23;
    const rightTextLength = 5;
    const remainingSpaces = maxCharsPerLineFontA - (leftTextLength + rightTextLength);
    return leftText + ' '.repeat(remainingSpaces) + rightText;
  };

  const wrapText = (text: string, indent: number, maxLineWidth: number): string => {
    const lines: string[] = [];
    let currentLine = '';
    let currentWidth = 0;

    for (const char of text) {
      const charWidth = /[^\x00-\x7F]/.test(char) ? 2 : 1;
      if (currentWidth + charWidth > maxLineWidth) {
        lines.push(currentLine);
        currentLine = ' '.repeat(indent) + char;
        currentWidth = indent + charWidth;
      } else {
        currentLine += char;
        currentWidth += charWidth;
      }
    }

    if (currentLine) lines.push(currentLine);
    return lines.join('\n');
  };

  function printPoweredBy(builder: PrinterBuilder) {
    builder.addTextAlign('center');
    builder.addText('Powered by tabilife.co.jp\n');
    builder.addFeedLine(1);
    builder.addCut('feed');
  }

  const print = async (builder: PrinterBuilder, data: IOrders | undefined, dataSettings: ISettings | undefined, stadium: IStadium | undefined, resolve: () => void) => {
    try {
      builder.addTextLang('ja');
      if (!data || Object.keys(data).length === 0) {
        await printStadiumInformation(builder, currentSetting, stadiumData);
      } else {
        await printOrder(builder, data, dataSettings, stadium);
      }

      const shouldSkipPoweredBy = stadium?.cms_settings?.pre_order || stadiumData?.cms_settings?.pre_order;
      if (!shouldSkipPoweredBy) {
        printPoweredBy(builder);
      } else {
        builder.addFeedLine(1);
        builder.addCut('feed');
      }

      await builder.send();
      resolve();
    } catch (error) {
      throw error;
    }
  };

  const printOrder = async (builder: PrinterBuilder, data: IOrders, dataSettings: ISettings | undefined, stadium: IStadium | undefined) => {
    const is_preorder = stadium?.cms_settings?.pre_order || stadiumData?.cms_settings?.pre_order;
    const { totalPrice, totalCount, totalTax10, totalTax8, totalTaxAmount10, totalTaxAmount8 } = calculateTotal(data);
    if (!is_preorder) {
      await printHeaderSection(builder, data, is_preorder, stadium);
      await printOrderDetails(builder, data, is_preorder);
      builder.addFeedLine(2);
      builder.addCut('feed');
    }
    if (is_preorder) {
      await printHeaderSection(builder, data, is_preorder, stadium);
      await printOrderDetails(builder, data, is_preorder);
      return;
    }
    await printReceiptDetails(builder, data, dataSettings, stadium);
    printTotalSection(data, builder, totalCount, totalPrice, totalTax10, totalTaxAmount10, totalTax8, totalTaxAmount8, dataSettings);
  };

  const lookupPluginIp = async (): Promise<string | null> => {
    try {
      const savedTarget = connection.printerTarget || localStorage.getItem('printer_target_v2');
      const savedBrand = (connection.printerBrand || localStorage.getItem('printer_brand_v2') || '').toLowerCase();
      if (!savedTarget || !savedBrand) return null;
      let plugin: any = null;
      try {
        plugin = savedBrand === 'star' ? StarXpandPlugin : EposPlugin;
      } catch (_) {}
      if (!plugin) plugin = (globalThis as any).StarXpandPlugin || (globalThis as any).EposPlugin;
      if (plugin && typeof plugin.findIpForTarget === 'function') {
        const r: any = await plugin.findIpForTarget({ target: savedTarget }).catch(() => null);
        return r && r.found && r.ip ? r.ip : null;
      }
    } catch (e) {
      console.warn('Plugin IP lookup failed', e);
    }
    return null;
  };

  const processOrder = async (printOrder: IPrintOrder): Promise<void> => {
    const { data, dataSettings, stadium, type, printer_brand } = printOrder;

    const targetBrand: PrinterBrand =
      (printer_brand as PrinterBrand | undefined) || connection.printerBrand || ((currentSetting as any)?.printer_brand as PrinterBrand | undefined) || 'epson';

    const withTimeout = <T>(p: Promise<T>, ms: number, name = 'operation'): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        let done = false;
        const timer = setTimeout(() => {
          if (!done) {
            done = true;
            reject(new Error(`${name} timed out after ${ms}ms`));
          }
        }, ms);
        p.then((v) => {
          if (!done) {
            done = true;
            clearTimeout(timer);
            resolve(v);
          }
        }).catch((err) => {
          if (!done) {
            done = true;
            clearTimeout(timer);
            reject(err);
          }
        });
      });

    try {
      if (connection.ensureInitialized) {
        await withTimeout(connection.ensureInitialized(), 3000, 'ensureInitialized');
      }

      const explicitIp = (printOrder as IPrintOrder)?.ip_printer || currentSetting.ip_printer || null;

      const tryConnect = async (target?: string, ipAddress?: string, skipBluetoothCheck = false) => {
        if (!connection.connectPrinter) return false;
        try {
          await withTimeout(connection.connectPrinter(targetBrand, target || '', ipAddress, skipBluetoothCheck), 5000, `connectPrinter(${target || ipAddress})`);
          return true;
        } catch {
          return false;
        }
      };

      let printed = false;

      if (connection.isConnected && connection.printerBrand === targetBrand) {
        try {
          const builder = new PrinterBuilder();
          await print(builder, data, dataSettings, stadium, () => {});
          printed = true;
          connection.markSuccessfulPrint();
        } catch (printErr: any) {
          console.warn('usePrinterNative: print failed with existing connection', printErr?.message);
          try {
            await connection.disconnectPrinter();
          } catch {}
        }
      }

      for (let btAttempt = 1; btAttempt <= BT_CONNECT_ATTEMPTS && !printed; btAttempt++) {
        if (connection.bluetoothEnabled) {
          const externalTarget = connection.printerTarget;
          if (externalTarget) {
            if (connection.isConnected && (connection.printerBrand !== targetBrand || connection.printerTarget !== externalTarget)) {
              try {
                await connection.disconnectPrinter();
                await new Promise((res) => setTimeout(res, 300));
              } catch {}
            }

            const needsConnect = !connection.isConnected || connection.printerBrand !== targetBrand || connection.printerTarget !== externalTarget;
            if (needsConnect && (await tryConnect(externalTarget))) {
              try {
                const builder = new PrinterBuilder();
                await print(builder, data, dataSettings, stadium, () => {});
                printed = true;
                connection.markSuccessfulPrint();
                break;
              } catch {}
            }
          }

          try {
            await withTimeout(connection.loadSavedPrinter(), 3000, 'loadSavedPrinter');
          } catch {}

          if (connection.printerTarget && (await tryConnect(connection.printerTarget))) {
            try {
              const builder = new PrinterBuilder();
              await print(builder, data, dataSettings, stadium, () => {});
              printed = true;
              connection.markSuccessfulPrint();
              break;
            } catch {}
          }

          const printers = discovery.discoveredPrinters || [];
          for (const p of printers) {
            const addr = p.address && String(p.address).trim() ? p.address : p.name;
            if (!addr) continue;
            if (await tryConnect(addr)) {
              try {
                const builder = new PrinterBuilder();
                await print(builder, data, dataSettings, stadium, () => {});
                printed = true;
                connection.markSuccessfulPrint();
                break;
              } catch {}
            }
          }
        } else {
          await new Promise((res) => setTimeout(res, BT_CONNECT_DELAY_MS));
        }
      }

      if (!printed) {
        const pluginIp: string | null = await lookupPluginIp();
        if (pluginIp) {
          for (let attempt = 1; attempt <= IP_CONNECT_ATTEMPTS && !printed; attempt++) {
            if (await tryConnect(undefined, pluginIp, true)) {
              try {
                const builder = new PrinterBuilder();
                await print(builder, data, dataSettings, stadium, () => {});
                printed = true;
                connection.markSuccessfulPrint();
                break;
              } catch {}
            }
            if (!printed) {
              await new Promise((res) => setTimeout(res, IP_CONNECT_DELAY_MS));
            }
          }
        }

        if (!printed && explicitIp) {
          for (let attempt = 1; attempt <= IP_CONNECT_ATTEMPTS && !printed; attempt++) {
            if (await tryConnect(undefined, explicitIp, true)) {
              try {
                const builder = new PrinterBuilder();
                await print(builder, data, dataSettings, stadium, () => {});
                printed = true;
                connection.markSuccessfulPrint();
                break;
              } catch {}
            }
            if (!printed) {
              await new Promise((res) => setTimeout(res, IP_CONNECT_DELAY_MS));
            }
          }
        }
      }

      if (!printed) {
        await addToQueue(printOrder);
        throw new Error('Enqueued for retry');
      }

      if (data?.id && type) {
        await mutateAsync({
          order_id: data.id,
          print_message: 'Print successful',
          type,
        });
      }
    } catch (error: any) {
      if (data?.id && type) {
        await mutateAsync({
          order_id: data.id,
          print_message: `Print error: ${error.message}`,
          type,
        });
      }
      throw error;
    }
  };

  const addToQueue = async (order: IPrintOrder) => {
    queue.addToQueue(order);
    queue.processQueue(async (item) => {
      await processOrder(item as IPrintOrder);
    });
  };

  const printViaIp = async (ip?: string): Promise<void> => {
    const printOrderObj: IPrintOrder = {
      ip_printer: ip || currentSetting.ip_printer || undefined,
    };

    const { data, dataSettings, stadium, type, printer_brand } = printOrderObj;

    const targetBrand: PrinterBrand =
      (printer_brand as PrinterBrand | undefined) || connection.printerBrand || ((currentSetting as any)?.printer_brand as PrinterBrand | undefined) || 'epson';

    const withTimeout = <T>(p: Promise<T>, ms: number, name = 'operation'): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        let done = false;
        const timer = setTimeout(() => {
          if (!done) {
            done = true;
            reject(new Error(`${name} timed out after ${ms}ms`));
          }
        }, ms);
        p.then((v) => {
          if (!done) {
            done = true;
            clearTimeout(timer);
            resolve(v);
          }
        }).catch((err) => {
          if (!done) {
            done = true;
            clearTimeout(timer);
            reject(err);
          }
        });
      });

    const tryConnect = async (ipAddress?: string) => {
      if (!ipAddress || !connection.connectPrinter) return false;
      try {
        await withTimeout(connection.connectPrinter(targetBrand, '', ipAddress, true), 5000, `connectPrinter(${ipAddress})`);
        return true;
      } catch {
        return false;
      }
    };

    try {
      if (connection.ensureInitialized) {
        await withTimeout(connection.ensureInitialized(), 3000, 'ensureInitialized');
      }

      const explicitIp = printOrderObj?.ip_printer || currentSetting.ip_printer || null;

      if (!explicitIp) {
        throw new Error('No explicit IP provided');
      }

      const connected = await tryConnect(explicitIp);
      if (!connected) {
        throw new Error(`Failed to connect to ${explicitIp}`);
      }

      const builder = new PrinterBuilder();
      await print(builder, data, dataSettings, stadium, () => {});

      if (data?.id && type) {
        await mutateAsync({
          order_id: data.id,
          print_message: 'Print successful (IP-only)',
          type,
        });
      }
    } catch (error: any) {
      if (data?.id && type) {
        await mutateAsync({
          order_id: data.id,
          print_message: `Print error: ${error.message}`,
          type,
        });
      }
      throw error;
    }
  };

  const reconnectSavedPrinter = async (): Promise<void> => {
    try {
      const platform = Capacitor?.getPlatform ? Capacitor.getPlatform() : ((window as any)?.Capacitor?.getPlatform?.() ?? 'web');
      if (platform === 'web') return;

      // Skip if already connected — avoids redundant connect after loadSavedPrinter
      if (connection.isConnected) return;

      const savedBrand = (localStorage.getItem('printer_brand_v2') || connection.printerBrand || '').toLowerCase() as PrinterBrand;
      const savedTarget = (localStorage.getItem('printer_target_v2') || connection.printerTarget || '') as string | null;
      if (!savedTarget) return;

      if (!connection.connectPrinter) return;

      const maxConnect = 3;
      for (let i = 1; i <= maxConnect; i++) {
        try {
          await connection.connectPrinter(savedBrand, savedTarget as any);
          break;
        } catch (err) {
          await new Promise((res) => setTimeout(res, 400 * i));
        }
      }
    } catch (e) {
      console.warn('reconnectSavedPrinter error', e);
    }
  };

  const prevBluetoothEnabledRef = useRef<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      try {
        const platform = Capacitor?.getPlatform ? Capacitor.getPlatform() : ((window as any)?.Capacitor?.getPlatform?.() ?? 'web');
        if (platform === 'web') return;
        const prev = prevBluetoothEnabledRef.current;
        const curr = connection.bluetoothEnabled;
        prevBluetoothEnabledRef.current = curr;
        if (prev === false && curr === true) {
          await reconnectSavedPrinter();
        }
      } catch (e) {
        console.warn('Auto reconnect failed', e);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [connection.bluetoothEnabled]);

  return {
    printerBrand: connection.printerBrand,
    printerTarget: connection.printerTarget,
    isConnected: connection.isConnected,
    isConnecting: connection.isConnecting,
    bluetoothEnabled: connection.bluetoothEnabled,
    permissionDenied: connection.permissionDenied,
    connectionError: connection.connectionError,
    isUnsupported: connection.isUnsupported,
    isScanning: discovery.isScanning,
    discoveredPrinters: discovery.discoveredPrinters,
    scanError: discovery.scanError,
    isPrinting: queue.isPrinting,
    queueLength: queue.queueLength,
    scanForPrinters: discovery.scanForPrinters,
    cancelScan: discovery.cancelScan,
    clearDiscovered: discovery.clearDiscovered,
    connectPrinter: connection.connectPrinter,
    disconnectPrinter: connection.disconnectPrinter,
    removePrinter: connection.removePrinter,
    addToQueue,
    printViaIp,
    reconnectSavedPrinter,
    getQueueStatus: queue.getQueueStatus,
    clearQueue: queue.clearQueue,
  };
};

export default usePrinterNative;
export type { DiscoveredPrinter } from './usePrinterDiscovery';
export { usePrinterNative as usePrinter };

